import Phaser from 'phaser';
import { GameState } from '../state/GameState';

interface InitData {
  // Where to go after the quiz finishes.
  next: 'LevelIntroScene' | 'BossScene' | 'WinScene';
  nextData?: { level?: number };
}

type Op = 'x' | '+' | '-' | '/';

interface Question {
  a: number;
  b: number;
  op: Op;
  answer: number;
  options: number[];
}

export class TimesTableScene extends Phaser.Scene {
  private next: InitData['next'] = 'LevelIntroScene';
  private nextData: InitData['nextData'] = {};
  private questions: Question[] = [];
  private current = 0;
  private correct = 0;
  private feedbackText!: Phaser.GameObjects.Text;
  private questionText!: Phaser.GameObjects.Text;
  private progressText!: Phaser.GameObjects.Text;
  private scoreText!: Phaser.GameObjects.Text;
  private optionButtons: Phaser.GameObjects.Container[] = [];
  private locked = false;

  constructor() {
    super('TimesTableScene');
  }

  init(data: InitData): void {
    this.next = data.next;
    this.nextData = data.nextData ?? {};
    this.questions = [];
    this.current = 0;
    this.correct = 0;
    this.locked = false;
    this.optionButtons = [];

    // 4 questions; ranges and weights vary with the chosen game difficulty.
    const r = ranges(GameState.getDifficulty(this.registry));
    const used = new Set<string>();
    while (this.questions.length < 4) {
      const op = pickWeightedOp(r);
      let q: Question | null = null;
      let key = '';
      if (op === 'x') {
        const a = Phaser.Math.Between(r.mulMin, r.mulMax);
        const b = Phaser.Math.Between(r.mulMin, r.mulMax);
        key = `x:${a}-${b}`;
        if (used.has(key)) continue;
        q = makeQuestion(a, b, 'x');
      } else if (op === '+') {
        const a = Phaser.Math.Between(r.addMin, r.addMax);
        const b = Phaser.Math.Between(r.addMin, r.addMax);
        key = `+:${a}-${b}`;
        if (used.has(key)) continue;
        q = makeQuestion(a, b, '+');
      } else if (op === '-') {
        let a = Phaser.Math.Between(r.subMin, r.subMax);
        let b = Phaser.Math.Between(r.subMin, r.subMax);
        if (b > a) [a, b] = [b, a];
        key = `-:${a}-${b}`;
        if (used.has(key)) continue;
        q = makeQuestion(a, b, '-');
      } else {
        const divisor = Phaser.Math.Between(r.divMin, r.divMax);
        const quotient = Phaser.Math.Between(r.divMin, r.divMax);
        const dividend = divisor * quotient;
        key = `/:${dividend}-${divisor}`;
        if (used.has(key)) continue;
        q = makeQuestion(dividend, divisor, '/');
      }
      used.add(key);
      this.questions.push(q);
    }
  }

  create(): void {
    const W = this.scale.width;
    const H = this.scale.height;
    this.add.rectangle(0, 0, W, H, 0x1d2434).setOrigin(0, 0);

    this.add
      .text(W / 2, 60, 'Math Bonus', {
        fontFamily: 'sans-serif',
        fontSize: '40px',
        color: '#f2c14e',
      })
      .setOrigin(0.5);

    this.progressText = this.add
      .text(W / 2, 110, '', {
        fontFamily: 'sans-serif',
        fontSize: '20px',
        color: '#bcd0e0',
      })
      .setOrigin(0.5);

    this.questionText = this.add
      .text(W / 2, H / 2 - 60, '', {
        fontFamily: 'sans-serif',
        fontSize: '88px',
        color: '#ffffff',
      })
      .setOrigin(0.5);

    this.feedbackText = this.add
      .text(W / 2, H / 2 + 40, '', {
        fontFamily: 'sans-serif',
        fontSize: '28px',
        color: '#ffd9a0',
      })
      .setOrigin(0.5);

    this.scoreText = this.add
      .text(W - 30, 30, '', {
        fontFamily: 'sans-serif',
        fontSize: '20px',
        color: '#bcd0e0',
      })
      .setOrigin(1, 0);

    this.showQuestion();
  }

  private showQuestion(): void {
    const q = this.questions[this.current];
    this.locked = false;
    this.feedbackText.setText('');
    this.progressText.setText(`Question ${this.current + 1} of 4`);
    const sym = symbolFor(q.op);
    this.questionText.setText(`${q.a} ${sym} ${q.b} = ?`);
    this.scoreText.setText(`Correct: ${this.correct}/${this.current}`);

    this.optionButtons.forEach((b) => b.destroy());
    this.optionButtons = [];

    const W = this.scale.width;
    const H = this.scale.height;
    const optionY = H / 2 + 130;
    q.options.forEach((opt, i) => {
      const x = W / 2 + (i - 1.5) * 150;
      const btn = this.add.container(x, optionY);
      const bg = this.add.rectangle(0, 0, 130, 70, 0x3d2914).setStrokeStyle(3, 0xffffff);
      const txt = this.add
        .text(0, 0, String(opt), {
          fontFamily: 'sans-serif',
          fontSize: '36px',
          color: '#ffffff',
        })
        .setOrigin(0.5);
      btn.add([bg, txt]);
      btn.setSize(130, 70).setInteractive({ useHandCursor: true });
      btn.on('pointerover', () => bg.setFillStyle(0x6b3410));
      btn.on('pointerout', () => bg.setFillStyle(0x3d2914));
      btn.on('pointerdown', () => this.answer(opt, bg));
      this.optionButtons.push(btn);
    });
  }

  private answer(opt: number, bg: Phaser.GameObjects.Rectangle): void {
    if (this.locked) return;
    this.locked = true;
    const q = this.questions[this.current];
    if (opt === q.answer) {
      this.correct += 1;
      bg.setFillStyle(0x2c8a4a);
      this.feedbackText.setText('Correct!').setColor('#9aeaa8');
    } else {
      bg.setFillStyle(0xc23b3b);
      const sym = symbolFor(q.op);
      this.feedbackText.setText(`Nope — ${q.a} ${sym} ${q.b} = ${q.answer}`).setColor('#ffb0b0');
      // Highlight the right answer too.
      this.optionButtons.forEach((b) => {
        const t = b.list[1] as Phaser.GameObjects.Text;
        if (t.text === String(q.answer)) {
          (b.list[0] as Phaser.GameObjects.Rectangle).setFillStyle(0x2c8a4a);
        }
      });
    }
    this.scoreText.setText(`Correct: ${this.correct}/${this.current + 1}`);

    this.time.delayedCall(1300, () => {
      this.current += 1;
      if (this.current >= this.questions.length) {
        this.finish();
      } else {
        this.showQuestion();
      }
    });
  }

  private finish(): void {
    const W = this.scale.width;
    const H = this.scale.height;
    this.optionButtons.forEach((b) => b.destroy());
    this.questionText.setText(`Score: ${this.correct} / 4`);
    this.feedbackText
      .setText('Great job! Next level coming up...')
      .setColor('#ffd9a0');
    this.add
      .text(W / 2, H - 60, 'Continuing...', {
        fontFamily: 'sans-serif',
        fontSize: '18px',
        color: '#888',
      })
      .setOrigin(0.5);
    this.time.delayedCall(1400, () => {
      this.scene.start(this.next, this.nextData);
    });
  }
}

function makeQuestion(a: number, b: number, op: Op): Question {
  const answer =
    op === 'x' ? a * b : op === '+' ? a + b : op === '-' ? a - b : a / b;
  // 3 distractor wrong answers near the correct value.
  // Distractor spread scales with the answer so 3-digit answers don't all
  // look the same.
  const spread = Math.max(12, Math.round(Math.abs(answer) * 0.15));
  const distractors = new Set<number>();
  while (distractors.size < 3) {
    const offset = Phaser.Math.Between(-spread, spread);
    const candidate = answer + offset;
    if (candidate > 0 && candidate !== answer) distractors.add(candidate);
  }
  const options = Phaser.Utils.Array.Shuffle([answer, ...distractors]);
  return { a, b, op, answer, options };
}

interface Ranges {
  mulMin: number;
  mulMax: number;
  addMin: number;
  addMax: number;
  subMin: number;
  subMax: number;
  divMin: number;
  divMax: number;
  // Weights (must sum to 100).
  wMul: number;
  wAdd: number;
  wSub: number;
  wDiv: number;
}

function ranges(d: 'easy' | 'medium' | 'hard'): Ranges {
  if (d === 'easy') {
    // Small numbers; mostly + and × up to 5.
    return {
      mulMin: 1,
      mulMax: 5,
      addMin: 1,
      addMax: 20,
      subMin: 1,
      subMax: 20,
      divMin: 1,
      divMax: 5,
      wMul: 35,
      wAdd: 50,
      wSub: 12,
      wDiv: 3,
    };
  }
  if (d === 'hard') {
    // 3-digit add/subtract, division dividends up to 15 × 15 = 225.
    return {
      mulMin: 2,
      mulMax: 12,
      addMin: 100,
      addMax: 999,
      subMin: 100,
      subMax: 999,
      divMin: 4,
      divMax: 15,
      wMul: 30,
      wAdd: 25,
      wSub: 22,
      wDiv: 23,
    };
  }
  // medium (default)
  return {
    mulMin: 1,
    mulMax: 12,
    addMin: 5,
    addMax: 50,
    subMin: 2,
    subMax: 50,
    divMin: 2,
    divMax: 12,
    wMul: 40,
    wAdd: 30,
    wSub: 18,
    wDiv: 12,
  };
}

function pickWeightedOp(r: Ranges): Op {
  const total = r.wMul + r.wAdd + r.wSub + r.wDiv;
  let pick = Phaser.Math.Between(1, total);
  if ((pick -= r.wMul) <= 0) return 'x';
  if ((pick -= r.wAdd) <= 0) return '+';
  if ((pick -= r.wSub) <= 0) return '-';
  return '/';
}

function symbolFor(op: Op): string {
  return op === 'x' ? '×' : op === '+' ? '+' : op === '-' ? '−' : '÷';
}
